/**
 * The slider for the whole app.
 *
 * Extracted at the third caller, the same threshold `SegmentedControl` was extracted at. Avatar
 * zoom and the two sound-fade sliders were each a bare `<input type='range'>` with their own
 * class string, and they had already drifted: one set a track height and the other did not, one
 * used `accent-ws-primary` and the other inherited whatever the browser picked.
 *
 * What this is NOT: the Slider BLOCK's student-facing control, which is a different thing
 * entirely — it carries tick marks, a value bubble, log scaling and an answer. This is editor
 * furniture for setting a number by dragging.
 *
 * A11y notes, because a range input gets two of them wrong by default:
 *
 *   - `aria-valuetext` whenever the number is not what the control means. A skew slider sitting
 *     at -30 is "Low 80%", and a screen reader left to read the raw value announces an
 *     implementation detail nobody set.
 *   - The visible readout is a sibling, not a label, so it can restate the value without
 *     becoming part of the accessible name — "Skew, Low 80%, slider" rather than "Skew Low 80%".
 */
import { useId } from 'react';

import type { ReactNode } from 'react';

export interface RangeFieldProps {
  value: number;
  onChange: (n: number) => void;
  min: number;
  max: number;
  step?: number;
  /** Visible label beside the track. Omit when the control is labelled by its surroundings and
   *  `ariaLabel` carries the name instead. */
  label?: ReactNode;
  ariaLabel?: string;
  /**
   * What the value MEANS, when the number alone does not say it. Rendered as the readout and
   * given to `aria-valuetext`, so the two cannot disagree.
   */
  valueText?: string;
  /** Width of the readout column. Fixed so dragging does not reflow the row under the pointer. */
  readoutClassName?: string;
  describedBy?: string;
  disabled?: boolean;
  /** Names the data key this control writes, so an undo can pulse it. */
  fieldId?: string;
  /** Phone full-screen: 36px instead of 24px, matching what the variables panel already does to
   *  its buttons there. 24px clears WCAG 2.2 on a mouse; a thumb under a thumb wants more. */
  roomy?: boolean;
  className?: string;
}

export function RangeField({
  value, onChange, min, max, step = 1,
  label, ariaLabel, valueText, readoutClassName = 'w-16',
  describedBy, disabled, fieldId, roomy, className,
}: RangeFieldProps) {
  const id = useId();
  return (
    <div className={`flex min-w-0 items-center gap-2 ${className ?? ''}`}>
      {label != null && (
        <label htmlFor={id} className='shrink-0 text-xs text-ws-muted'>{label}</label>
      )}
      <input
        id={id}
        type='range'
        min={min}
        max={max}
        step={step}
        value={value}
        disabled={disabled}
        data-field-id={fieldId}
        onChange={e => onChange(Number(e.target.value))}
        aria-label={ariaLabel}
        aria-valuetext={valueText}
        aria-describedby={describedBy}
        /*
         * `h-6`, not the `h-1.5` the app's hand-rolled sliders used: the element's own box IS the
         * touch target, and a 6px-tall one is a quarter of the 24px WCAG 2.2 asks for and a
         * seventh of what a thumb on a phone actually needs. The browser still draws the thin
         * track centred in the taller box, so this costs height, not a thicker-looking slider.
         *
         * `touch-none` so dragging the thumb does not also scroll the panel out from under it —
         * the variables panel is scrollable and this control is dragged horizontally inside it.
         */
        className={`${roomy ? 'h-9' : 'h-6'} min-w-0 flex-1 touch-none accent-ws-primary disabled:cursor-not-allowed disabled:opacity-50`} />
      {valueText != null && (
        <span aria-hidden='true' className={`${readoutClassName} shrink-0 text-right text-xs tabular-nums text-ws-muted`}>
          {valueText}
        </span>
      )}
    </div>
  );
}
